import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Activity, Zap, Filter, ListChecks, History, Radar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { SignalCard, SignalCardSkeleton, EmptySignals } from "@/components/SignalCard";
import NewHedgeModal from "@/components/NewHedgeModal";
import { shortDate } from "@/lib/format";
import "./Signals.css";

const DISCOVERY_CAP = 50; // birikim listesi max

export default function Signals() {
  const qc = useQueryClient();
  const [minConf, setMinConf] = useState(30);
  const [scanning, setScanning] = useState(false);
  const [hedgeOpen, setHedgeOpen] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [autoScan, setAutoScan] = useState(true);
  // Geçmiş keşifler — sembol bazında deduplike, daha yüksek güvenle güncellenir
  const [discoveries, setDiscoveries] = useState([]);
  const lastDiscoveryAtRef = useRef(0);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });

  const { data: live, isLoading: liveLoading, refetch: refetchLive } = useQuery({
    queryKey: ["signals-live", minConf, settings?.preferred_counter_exchange],
    queryFn: () =>
      api.signalsLive({
        min_confidence: minConf,
        counter_exchange: settings?.preferred_counter_exchange || "BINANCE",
      }),
    refetchInterval: autoScan ? 5000 : false,
    enabled: !!settings,
  });

  // Live tarama sonuçları geldikçe `discoveries` listesini güncelle —
  // aynı sembol tekrar görünürse güvenle birlikte timestamp'i yenile,
  // yeni semboller listenin başına eklenir, max 50 tutulur.
  // Bu birikim havuzu (önceki taramaları da tutar), saf derived state ile
  // elde edilemez; bu yüzden useEffect doğru patterndır.
  useEffect(() => {
    const sigs = live?.signals;
    if (!Array.isArray(sigs) || sigs.length === 0) return;
    const at = Date.now();
    lastDiscoveryAtRef.current = at;
    setDiscoveries((prev) => {
      const map = new Map(prev.map((d) => [d.symbol, d]));
      sigs.forEach((s) => {
        const existing = map.get(s.symbol);
        map.set(s.symbol, {
          ...s,
          firstSeenAt: existing?.firstSeenAt || at,
          lastSeenAt: at,
          hitCount: (existing?.hitCount || 0) + 1,
        });
      });
      // En son görülenler üstte
      return Array.from(map.values())
        .sort((a, b) => b.lastSeenAt - a.lastSeenAt)
        .slice(0, DISCOVERY_CAP);
    });
  }, [live]);

  const { data: persisted } = useQuery({
    queryKey: ["signals-history"],
    queryFn: () => api.listSignals(30),
    refetchInterval: 10000,
  });

  const scanMut = useMutation({
    mutationFn: () => api.scanFlow(minConf),
    onMutate: () => setScanning(true),
    onSettled: () => setScanning(false),
    onSuccess: (data) => {
      toast.success(`${data.count} sinyal kaydedildi`);
      qc.invalidateQueries({ queryKey: ["signals-live"] });
      qc.invalidateQueries({ queryKey: ["signals-history"] });
    },
    onError: (e) => toast.error(`Tarama hatası: ${errMsg(e)}`),
  });

  const sortedSignals = useMemo(() => {
    if (!live?.signals) return [];
    return [...live.signals].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }, [live]);

  const pendingPersisted = useMemo(
    () => (persisted || []).filter((s) => s.status === "PENDING"),
    [persisted]
  );

  const handleOpen = (signal) => {
    // Modal aç — kullanıcı tutarı/hedef kârı/kaldıracı kendi seçsin
    setSelectedSignal(signal);
    setHedgeOpen(true);
  };

  return (
    <div className="p-5 md:p-6 space-y-5 w-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight" data-testid="signals-page-title">
            Sinyal Motoru
          </h1>
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            Order Flow · Derinlik · Hacim · Akıllı Yön
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setAutoScan((v) => !v)}
            data-testid="signals-autoscan-toggle"
            className={`h-9 px-3 border text-[10px] uppercase tracking-widest rounded-sm flex items-center gap-2 transition ${
              autoScan
                ? "border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-300"
                : "border-white/10 bg-white/[0.02] text-zinc-400 hover:bg-white/5"
            }`}
          >
            <Radar className={`w-3.5 h-3.5 ${autoScan ? "sx-radar-spin" : ""}`} />
            {autoScan ? "Otomatik Tarama Aktif" : "Otomatik Tarama Kapalı"}
          </button>
          <div className="flex items-center gap-2 border border-white/10 px-3 py-1.5 bg-white/[0.02]">
            <Filter className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Min Güven</span>
            <input
              data-testid="min-confidence-input"
              type="range"
              min="0"
              max="100"
              step="5"
              value={minConf}
              onChange={(e) => setMinConf(Number(e.target.value))}
              className="w-24 accent-white"
            />
            <span className="font-mono text-xs text-white w-7 text-right">{minConf}</span>
          </div>
          <Button
            onClick={() => refetchLive()}
            variant="outline"
            data-testid="signals-refresh-btn"
            className="h-9 px-3 border-white/10 hover:bg-white/5 text-xs uppercase tracking-widest rounded-sm"
          >
            <Activity className="w-3.5 h-3.5 mr-1.5" />
            Yenile
          </Button>
          <Button
            onClick={() => scanMut.mutate()}
            disabled={scanning}
            data-testid="signals-scan-flow-btn"
            className="h-9 px-4 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-widest font-semibold rounded-sm"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            {scanning ? "Taranıyor…" : "Şimdi Tara"}
          </Button>
        </div>
      </div>

      {/* Info banner — premium style */}
      <div className="tx-note">
        <div className="nic" aria-hidden>
          <svg viewBox="0 0 24 24">
            <path d="M12 3 2 20h20L12 3z" />
            <path d="M12 10v4M12 17h.01" strokeLinecap="round" />
          </svg>
        </div>
        <p>
          Sistem <b>10 sembolü</b> sürekli tarıyor. Her sinyalde yön
          (LONG/SHORT), giriş/stop/hedef, R/R ve güven skoru hesaplanır. Bu
          test aşamasında düşük eşik (≥{minConf}) ile <b>tüm kârlı fırsatlar</b>
          {" "}listelenir. Pozisyon açıldığında Darkex{" "}
          <span className="k">KAYBEDEN</span> tarafa, karşı borsa{" "}
          <span className="w">KAZANAN</span> tarafa girer.
        </p>
      </div>

      {/* Live signal feed + Discoveries — yan yana, full width */}
      <div className="sx-scan-grid">
        {/* SOL: Anlık tarayıcı çıktısı */}
        <div className="sx-pane">
          <div className="tx-scan">
            <div className="l">
              <span className={`dt ${autoScan ? "sx-dt-pulse" : ""}`} />
              <h3>Canlı Fırsatlar</h3>
              <span className="n">· {sortedSignals.length} sembol</span>
            </div>
            {live?.scanned_at && (
              <div className="t">
                <svg viewBox="0 0 24 24">
                  <path d="M21 12a9 9 0 1 1-6.2-8.5" strokeLinecap="round" />
                </svg>
                {shortDate(live.scanned_at)} taranıyor
              </div>
            )}
          </div>

          {/* Scanner laser stripe */}
          {autoScan && (
            <div className="sx-laser" aria-hidden>
              <div className="sx-laser-bar" />
            </div>
          )}

          {liveLoading && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <SignalCardSkeleton key={i} />)}
            </div>
          )}

          {!liveLoading && sortedSignals.length === 0 && (
            <EmptySignals onScan={() => scanMut.mutate()} scanning={scanning} />
          )}

          <div className="space-y-2">
            {sortedSignals.map((s) => (
              <SignalCard key={s.symbol} signal={s} onOpen={handleOpen} />
            ))}
          </div>
        </div>

        {/* SAĞ: Birikimli keşif listesi */}
        <div className="sx-pane">
          <div className="tx-scan">
            <div className="l">
              <span className={`dt ${autoScan ? "sx-dt-pulse" : ""}`} />
              <h3>Bulunan Fırsatlar (Birikim)</h3>
              <span className="n">· {discoveries.length}/{DISCOVERY_CAP}</span>
            </div>
            {discoveries.length > 0 && (
              <button
                type="button"
                onClick={() => setDiscoveries([])}
                data-testid="signals-clear-discoveries"
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition"
              >
                Temizle
              </button>
            )}
          </div>

          <div className="sx-disc-list" data-testid="discoveries-list">
            {discoveries.length === 0 && (
              <div className="sx-disc-empty">
                {autoScan
                  ? "Tarayıcı çalışıyor… bulduğu her fırsat burada birikecek."
                  : "Otomatik tarama kapalı. Üstten aç ya da Şimdi Tara'ya bas."}
              </div>
            )}
            {discoveries.map((d) => (
              <div
                key={d.symbol}
                data-testid={`discovery-${d.symbol}`}
                className="sx-disc-row"
              >
                <div className="sx-disc-l">
                  <span className={`sx-disc-dir sx-${(d.direction || "—").toLowerCase()}`}>
                    {d.direction || "—"}
                  </span>
                  <span className="sx-disc-sym">{d.symbol}</span>
                  <span className="sx-disc-conf">
                    Güven <b>{Math.round(d.confidence || 0)}</b>
                  </span>
                  <span className="sx-disc-meta">×{d.hitCount}</span>
                  {d.entry && (
                    <span className="sx-disc-meta">@ ${Number(d.entry).toFixed(4)}</span>
                  )}
                </div>
                <div className="sx-disc-r">
                  <span className="sx-disc-time">
                    {shortDate(new Date(d.lastSeenAt).toISOString())}
                  </span>
                  <Button
                    onClick={() => handleOpen(d)}
                    data-testid={`discovery-open-${d.symbol}`}
                    className="h-7 px-3 bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] uppercase tracking-widest font-semibold rounded-sm"
                  >
                    Aç
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pending persisted signals */}
      {pendingPersisted.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-3.5 h-3.5 text-amber-400" />
            <h2 className="text-sm font-medium text-white">Bekleyen Sinyaller</h2>
            <span className="text-[10px] tracking-widest uppercase text-zinc-500">
              · Telegram veya panelden onay bekliyor
            </span>
          </div>
          <div className="space-y-2">
            {pendingPersisted.map((s) => (
              <div
                key={s.id}
                data-testid={`pending-signal-${s.id}`}
                className="border border-amber-400/20 bg-amber-400/[0.03] px-4 py-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-widest text-amber-400">Bekliyor</span>
                  <span className="font-mono text-sm">{s.symbol}</span>
                  <span className="text-[10px] tracking-widest uppercase text-zinc-500">
                    {s.direction || "—"} · Güven {s.confidence || s.score}
                  </span>
                  {s.entry && (
                    <span className="text-[10px] text-zinc-500 font-mono">
                      @ ${Number(s.entry).toFixed(4)}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => handleOpen(s)}
                    data-testid={`pending-accept-${s.id}`}
                    className="h-7 px-3 bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] uppercase tracking-widest font-semibold rounded-sm"
                  >
                    Aç
                  </Button>
                  <Button
                    onClick={() =>
                      api.respondSignal(s.id, "HAYIR").then(() => {
                        toast.success("Geçildi");
                        qc.invalidateQueries({ queryKey: ["signals-history"] });
                      })
                    }
                    data-testid={`pending-reject-${s.id}`}
                    variant="ghost"
                    className="h-7 px-3 text-[10px] uppercase tracking-widest text-zinc-400 hover:bg-white/5 rounded-sm"
                  >
                    Geç
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <History className="w-3.5 h-3.5 text-zinc-500" />
          <h2 className="text-sm font-medium text-white">Geçmiş Sinyaller</h2>
        </div>
        <div className="border border-white/[0.06]">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Zaman</th>
                <th className="text-left px-3 py-2 font-medium">Sembol</th>
                <th className="text-left px-3 py-2 font-medium">Yön</th>
                <th className="text-right px-3 py-2 font-medium">Güven</th>
                <th className="text-right px-3 py-2 font-medium">Giriş</th>
                <th className="text-right px-3 py-2 font-medium">R/R</th>
                <th className="text-left px-3 py-2 font-medium">Durum</th>
              </tr>
            </thead>
            <tbody>
              {(persisted || []).slice(0, 15).map((s) => (
                <tr
                  key={s.id}
                  data-testid={`history-row-${s.id}`}
                  className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                >
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">
                    {shortDate(s.created_at)}
                  </td>
                  <td className="px-3 py-2 font-mono">{s.symbol}</td>
                  <td className="px-3 py-2">
                    <span className={s.direction === "UP" ? "text-emerald-400" : s.direction === "DOWN" ? "text-rose-400" : "text-zinc-500"}>
                      {s.direction || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {s.confidence || s.score}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-400">
                    {s.entry ? Number(s.entry).toFixed(4) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-400">
                    {s.risk_reward ? `1:${s.risk_reward.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={s.status} />
                  </td>
                </tr>
              ))}
              {(!persisted || persisted.length === 0) && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-zinc-500">
                    Henüz kaydedilmiş sinyal yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hedge modal — sinyalden tetiklenir, kullanıcı parametre seçer */}
      <NewHedgeModal
        open={hedgeOpen}
        onClose={() => { setHedgeOpen(false); setSelectedSignal(null); }}
        initialSymbol={selectedSignal?.symbol}
        initialCounter={selectedSignal?.counter_exchange || settings?.preferred_counter_exchange}
      />
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    PENDING: { c: "text-amber-400 bg-amber-500/10", l: "Bekliyor" },
    ACCEPTED: { c: "text-emerald-400 bg-emerald-500/10", l: "Açıldı" },
    REJECTED: { c: "text-zinc-500 bg-white/5", l: "Geçildi" },
    EXPIRED: { c: "text-zinc-600 bg-white/5", l: "Süresi Doldu" },
  };
  const meta = map[status] || map.PENDING;
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${meta.c}`}>
      {meta.l}
    </span>
  );
};
